import {TileEdge, TileCorner, isCornerBuildingKnight} from './Tile'
import {MapValidator} from './MapValidator'
import {PlayerColor} from '@colonist/game-states'

export class MapStateDepthFirstSearch {
    private readonly player: PlayerColor
    private readonly edgeMap: TileEdge[]
    private readonly mapState: MapValidator
    private readonly ignoreOpponentBuildings: boolean
    private readonly ignoreMyBuildings: boolean

    longestConnectedRoadEdges: TileEdge[]
    allConnectedTileEdges: TileEdge[]
    interConnectedCornerBuildings: number[]
    interConnectedEmptyNodes: number[]
    interConnectedEnemyBuildings: number[]

    constructor(player: PlayerColor, edgeMap: TileEdge[], startingEdge: TileEdge, mapState: MapValidator, ignoreOpponentBuildings: boolean = false, ignoreMyBuildings: boolean = true) {
        this.player = player
        this.edgeMap = edgeMap
        this.mapState = mapState
        this.ignoreOpponentBuildings = ignoreOpponentBuildings
        this.ignoreMyBuildings = ignoreMyBuildings

        this.longestConnectedRoadEdges = []
        this.allConnectedTileEdges = []
        this.interConnectedCornerBuildings = []
        this.interConnectedEmptyNodes = []
        this.interConnectedEnemyBuildings = []

        const startingConnectPath: TileEdge[] = []
        this.traverseConnectedPathUsingDepthFirstSearch(startingEdge, startingConnectPath)
    }

    private traverseConnectedPathUsingDepthFirstSearch(currentEdge: TileEdge, connectedPath: TileEdge[], previousCorner?: TileCorner) {
        if(connectedPath.includes(currentEdge)) return
        if(currentEdge.owner != this.player) return

        connectedPath.push(currentEdge)
        if(connectedPath.length > this.longestConnectedRoadEdges.length) this.longestConnectedRoadEdges = [...connectedPath]

        if(!this.allConnectedTileEdges.includes(currentEdge)) this.allConnectedTileEdges.push(currentEdge)

        // Getting neighbors of current edge
        for(const endPoint of currentEdge.hexEdge.endPoints()) {
            const corner = this.mapState.tileState.getTileCorner(endPoint)
            if(corner == undefined) continue

            const cornerIndex = this.mapState.tileState.getTileCornerIndex(corner.hexCorner)
            if(cornerIndex == undefined) continue

            // store settlements connected by the route
            const isMyConnectedCornerBuildingUntracked = corner.owner == this.player
            if(isMyConnectedCornerBuildingUntracked) this.interConnectedCornerBuildings.push(cornerIndex)

            const isMyConnectedCornerEmptyUntracked = corner.owner == undefined && !this.interConnectedEmptyNodes.includes(cornerIndex)
            if(isMyConnectedCornerEmptyUntracked) this.interConnectedEmptyNodes.push(cornerIndex)

            // check if path is being broken by opponent building
            const isCornerOwnedByOpponent = corner.owner != this.player && corner.owner != undefined
            const isEnemyCornerBuildingUntracked = isCornerOwnedByOpponent && !this.interConnectedEnemyBuildings.includes(cornerIndex)
            if(isEnemyCornerBuildingUntracked) this.interConnectedEnemyBuildings.push(cornerIndex)
            if(!this.ignoreOpponentBuildings && isCornerOwnedByOpponent) continue

            // check if path is being broken by my buildings
            const isCornerOwnedByMe = corner.owner == this.player
            if(!this.ignoreMyBuildings && isCornerOwnedByMe) continue

            // Check that we're not doubling back on the previous path
            const areWeDoublingBack = previousCorner != undefined && previousCorner.sameCorner(corner)
            if(areWeDoublingBack) continue

            for(const hexEdge of corner.hexCorner.touchingEdges()) {
                const edge = this.mapState.tileState.getTileEdge(hexEdge)
                if(edge == undefined) continue
                if(!this.edgeMap.includes(edge)) continue
                if(!this.isEdgeTransitionContinuous(edge, currentEdge, corner)) continue

                this.traverseConnectedPathUsingDepthFirstSearch(edge, connectedPath, corner)
            }
        }
        connectedPath.pop()
    }

    private isEdgeTransitionContinuous(newEdge: TileEdge, oldEdge: TileEdge, connectingCorner: TileCorner): boolean {
        if(newEdge.type == oldEdge.type) return true
        if(isCornerBuildingKnight(connectingCorner.buildingType)) return false
        if(connectingCorner.owner == this.player) return true

        return false
    }
}
